// src/components/Dashboard/IglooSessionTimeline.js
import React, { useState, useEffect, useCallback } from 'react';
import { app } from '../../firebase/firebaseConfig';
import {
  doc,
  getDoc,
  getDocs,
  getFirestore,
  collection,
  query,
  where,
  limit,
  onSnapshot
} from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  ArrowLeft,
  Snowflake,
  UserPlus,
  UserMinus,
  Users,
  Flag,
  RefreshCw,
  AlertTriangle,
  Clock
} from 'lucide-react';
import { getGapDisplay } from './timelineUtils';

// Format timestamp to readable format
function formatTimestamp(timestamp) {
  if (!timestamp) return '-';
  const ms = timestamp.toMillis ? timestamp.toMillis() : timestamp;
  const date = new Date(ms);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

// Format relative time
function formatRelativeTime(timestamp) {
  if (!timestamp) return '-';
  const ms = timestamp.toMillis ? timestamp.toMillis() : timestamp;
  const now = Date.now();
  const diffMs = now - ms;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// Format duration in seconds to human readable
function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '-';

  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (seconds < 60) return `${seconds}s`;
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${hours}h ${minutes % 60}m`;
}

// Calculate duration from start to end (or now if ongoing)
function calculateDuration(startTimestamp, endTimestamp) {
  const startMs = startTimestamp?.toMillis ? startTimestamp.toMillis() : startTimestamp;
  const endMs = endTimestamp ? (endTimestamp.toMillis ? endTimestamp.toMillis() : endTimestamp) : Date.now();
  return Math.floor((endMs - startMs) / 1000);
}

// Build events array from live session participants (for live sessions that don't have pre-built events)
function buildEventsFromParticipants(participants) {
  const events = [];

  for (const p of participants) {
    for (const interval of (p.intervals || [])) {
      events.push({
        type: 'joined',
        familyId: p.familyId,
        at: interval.joinedAt
      });
      if (interval.leftAt) {
        events.push({
          type: 'left',
          familyId: p.familyId,
          at: interval.leftAt
        });
      }
    }
  }

  // Sort by timestamp
  events.sort((a, b) => {
    const aTime = a.at?.toMillis ? a.at.toMillis() : a.at;
    const bTime = b.at?.toMillis ? b.at.toMillis() : b.at;
    return aTime - bTime;
  });

  return events;
}

// Get currently active participants from live session
function getCurrentParticipants(participants) {
  return participants.filter(p => {
    const lastInterval = p.intervals?.[p.intervals.length - 1];
    return lastInterval && !lastInterval.leftAt;
  }).map(p => p.familyId);
}

function IglooSessionTimeline({ iglooId, entryTimestamp, onBack }) {
  const [session, setSession] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [familyNames, setFamilyNames] = useState({});
  const [iglooName, setIglooName] = useState(null);
  // Track if we need to search history (used when live session ends)
  const [needsHistorySearch, setNeedsHistorySearch] = useState(false);

  // Resolve family name from familyInfo collection
  const resolveFamilyName = useCallback(async (db, familyId) => {
    try {
      const familyDoc = await getDoc(doc(db, 'familyInfo', familyId));
      if (familyDoc.exists()) {
        return familyDoc.data().name || familyId;
      }
    } catch (err) {
      console.error(`Error resolving family name for ${familyId}:`, err);
    }
    return familyId;
  }, []);

  // Resolve igloo name from iglooInfo -> controllers -> familyInfo/.../igloos/
  const resolveIglooName = useCallback(async (db, iglooId) => {
    try {
      // First get the controller family from iglooInfo
      const iglooInfoDoc = await getDoc(doc(db, 'iglooInfo', iglooId));
      if (iglooInfoDoc.exists()) {
        const controllers = iglooInfoDoc.data().controllers || [];
        if (controllers.length > 0) {
          const controllerFamilyId = controllers[0];
          // Now get the igloo name from the controller's igloos subcollection
          const iglooNameDoc = await getDoc(
            doc(db, 'familyInfo', controllerFamilyId, 'igloos', iglooId)
          );
          if (iglooNameDoc.exists()) {
            return iglooNameDoc.data().name || iglooId;
          }
        }
      }
    } catch (err) {
      console.error(`Error resolving igloo name for ${iglooId}:`, err);
    }
    return iglooId;
  }, []);

  // Resolve all family names for participants
  const resolveAllFamilyNames = useCallback(async (db, participantFamilyIds) => {
    const names = {};
    await Promise.all(
      participantFamilyIds.map(async (familyId) => {
        names[familyId] = await resolveFamilyName(db, familyId);
      })
    );
    return names;
  }, [resolveFamilyName]);

  // Find history session - separated into its own function to avoid closure issues
  const findHistorySession = useCallback(async () => {
    console.log('IglooSessionTimeline: Searching history for iglooId:', iglooId, 'entryTimestamp:', entryTimestamp);
    setLoading(true);
    setError(null);

    try {
      const db = getFirestore(app);

      // First, get ALL history sessions for this igloo (without ordering to avoid composite index requirement)
      // We'll sort client-side
      const historyQuery = query(
        collection(db, 'iglooSessionsHistory'),
        where('iglooId', '==', iglooId),
        limit(100)
      );

      console.log('IglooSessionTimeline: Executing history query...');
      const historySnapshot = await getDocs(historyQuery);
      console.log('IglooSessionTimeline: Found', historySnapshot.size, 'history sessions');

      // Sort by startedAt descending on client
      const sortedDocs = historySnapshot.docs.sort((a, b) => {
        const aStart = a.data().startedAt?.toMillis ? a.data().startedAt.toMillis() : a.data().startedAt;
        const bStart = b.data().startedAt?.toMillis ? b.data().startedAt.toMillis() : b.data().startedAt;
        return bStart - aStart;
      });

      for (const docSnap of sortedDocs) {
        const data = docSnap.data();
        const startMs = data.startedAt?.toMillis ? data.startedAt.toMillis() : data.startedAt;
        const endMs = data.endedAt?.toMillis ? data.endedAt.toMillis() : data.endedAt;

        console.log('IglooSessionTimeline: Checking session', docSnap.id, 'start:', startMs, 'end:', endMs, 'entry:', entryTimestamp);

        // Check if entry timestamp falls within this session (with some tolerance)
        if (entryTimestamp >= startMs - 60000 && entryTimestamp <= endMs + 60000) {
          console.log('IglooSessionTimeline: Found matching history session:', docSnap.id);
          setSession({ id: docSnap.id, ...data });
          setIsLive(false);

          // Resolve family names
          const familyIds = data.participantFamilyIds || data.participants?.map(p => p.familyId) || [];
          const names = await resolveAllFamilyNames(db, familyIds);
          setFamilyNames(names);

          setLoading(false);
          setNeedsHistorySearch(false);
          return;
        }
      }

      // If we get here, we couldn't find a matching session
      console.log('IglooSessionTimeline: No matching history session found');
      setError('Could not find igloo session for this time period');
      setLoading(false);
      setNeedsHistorySearch(false);

    } catch (err) {
      console.error('IglooSessionTimeline: Error finding history session:', err);
      setError(err.message);
      setLoading(false);
      setNeedsHistorySearch(false);
    }
  }, [iglooId, entryTimestamp, resolveAllFamilyNames]);

  // Effect to handle history search when live session ends
  useEffect(() => {
    if (needsHistorySearch) {
      // Small delay to allow Firestore to process the session move
      const timeoutId = setTimeout(() => {
        findHistorySession();
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [needsHistorySearch, findHistorySession]);

  // Find and load the session
  useEffect(() => {
    if (!iglooId) return;

    const db = getFirestore(app);
    let unsubscribe = null;

    const findSession = async () => {
      setLoading(true);
      setError(null);
      setNeedsHistorySearch(false);

      try {
        // Resolve igloo name
        const resolvedIglooName = await resolveIglooName(db, iglooId);
        setIglooName(resolvedIglooName);

        // First, check if there's a live session for this igloo
        const liveSessionRef = doc(db, 'iglooSessionsLive', iglooId);
        const liveSessionDoc = await getDoc(liveSessionRef);

        if (liveSessionDoc.exists()) {
          const liveData = liveSessionDoc.data();

          // Check if the entry timestamp falls within this session
          const sessionStartMs = liveData.startedAt?.toMillis ? liveData.startedAt.toMillis() : liveData.startedAt;
          const entryMs = entryTimestamp;

          // If entry is after session start, this is likely the right session
          if (entryMs >= sessionStartMs - 60000) { // 1 minute tolerance
            console.log('IglooSessionTimeline: Found live session, setting up listener');
            setSession({ id: iglooId, ...liveData });
            setIsLive(true);

            // Resolve family names
            const familyIds = liveData.participants?.map(p => p.familyId) || [];
            const names = await resolveAllFamilyNames(db, familyIds);
            setFamilyNames(names);

            // Set up real-time listener for live session
            unsubscribe = onSnapshot(liveSessionRef, async (snapshot) => {
              if (snapshot.exists()) {
                const updatedData = snapshot.data();
                setSession({ id: iglooId, ...updatedData });

                // Check for new participants and resolve their names
                const newFamilyIds = updatedData.participants?.map(p => p.familyId) || [];
                setFamilyNames(prevNames => {
                  const newNames = { ...prevNames };
                  let hasNewNames = false;
                  for (const fid of newFamilyIds) {
                    if (!newNames[fid]) {
                      // Mark that we need to resolve this name
                      hasNewNames = true;
                      resolveFamilyName(db, fid).then(name => {
                        setFamilyNames(prev => ({ ...prev, [fid]: name }));
                      });
                    }
                  }
                  return hasNewNames ? newNames : prevNames;
                });
              } else {
                // Session ended - trigger history search
                console.log('IglooSessionTimeline: Live session ended, will search history');
                setIsLive(false);
                setNeedsHistorySearch(true);
              }
            });

            setLoading(false);
            return;
          }
        }

        // If not found in live or entry timestamp doesn't match, search history
        console.log('IglooSessionTimeline: No live session found, searching history');
        await findHistorySession();

      } catch (err) {
        console.error('IglooSessionTimeline: Error finding session:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    findSession();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [iglooId, entryTimestamp, resolveIglooName, resolveAllFamilyNames, resolveFamilyName, findHistorySession]);

  // Get events array (pre-built for history, constructed for live) - reversed so most recent is first
  const events = (session?.events || buildEventsFromParticipants(session?.participants || [])).slice().reverse();

  // Get current participants for live sessions
  const currentParticipants = isLive ? getCurrentParticipants(session?.participants || []) : [];

  // Calculate session duration
  const durationSeconds = session?.durationSeconds || calculateDuration(session?.startedAt, session?.endedAt);

  return (
    <Card className="h-full">
      <CardHeader className="py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack} className="h-8 w-8 p-0">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Snowflake className="w-5 h-5 text-cyan-500" />
              {iglooName || iglooId}
            </CardTitle>
            {isLive ? (
              <Badge variant="default" className="bg-green-500 animate-pulse">
                Live
              </Badge>
            ) : (
              <Badge variant="secondary">
                Ended
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            {/* Duration */}
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{isLive ? `${formatDuration(durationSeconds)} (ongoing)` : formatDuration(durationSeconds)}</span>
            </div>

            {/* Peak concurrent */}
            <div className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              <span>Peak: {session?.peakConcurrent || 0}</span>
            </div>

            {/* Total participants */}
            <span>{session?.totalParticipants || session?.participants?.length || 0} families</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" />
            <span>Loading session...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-destructive">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
            <p>Error: {error}</p>
          </div>
        ) : !session ? (
          <div className="text-center py-8 text-muted-foreground">
            <Snowflake className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No session data found.</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline vertical line — positioned at center of icon column */}
            <div className="absolute left-[76px] top-0 bottom-0 w-0.5 bg-border" />

            {/* Currently in session indicator (for live sessions) - at top */}
            {isLive && currentParticipants.length > 0 && (
              <div className="mb-4 ml-14 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-sm font-medium text-green-700">
                    Currently in session:
                  </span>
                  <span className="text-sm text-green-600">
                    {currentParticipants.map(fid => familyNames[fid] || fid).join(', ')}
                  </span>
                </div>
              </div>
            )}

            {/* Session ended event (for completed sessions) - at top */}
            {!isLive && session.endedAt && (
              <div className="flex items-start mb-4">
                <div className="w-14 shrink-0" />
                <div className="relative z-10 flex items-center justify-center w-10 h-10 rounded-full border-2 bg-gray-100 border-gray-300">
                  <Flag className="w-4 h-4 text-gray-500" />
                </div>
                <div className="flex-1 min-w-0 pl-3 py-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-500">Session Ended</span>
                    <span className="text-xs text-muted-foreground">
                      {formatTimestamp(session.endedAt)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ({formatRelativeTime(session.endedAt)})
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Events (reversed - most recent first) */}
            {(() => {
              const getTs = (e) => e.at?.toMillis ? e.at.toMillis() : e.at;

              return events.map((event, index) => {
                const name = familyNames[event.familyId] || event.familyId;
                const isJoin = event.type === 'joined';
                const prevEvent = index > 0 ? events[index - 1] : null;

                let gap = null;
                if (prevEvent) {
                  const gapMs = Math.abs(getTs(prevEvent) - getTs(event));
                  gap = getGapDisplay(gapMs);
                }

                return (
                  <React.Fragment key={`${event.familyId}-${index}`}>
                    {/* Gap row */}
                    {gap?.label && (
                      <div className={`flex items-center ${gap.heightClass}`}>
                        <div className="w-14 shrink-0 text-right pr-3">
                          <span className="text-[10px] font-mono text-muted-foreground">{gap.label}</span>
                        </div>
                        <div className="w-10 shrink-0" />
                        <div className="flex-1" />
                      </div>
                    )}

                    {/* Event row */}
                    <div className="flex items-start">
                      <div className="w-14 shrink-0" />
                      <div className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                        isJoin
                          ? 'bg-green-100 border-green-300'
                          : 'bg-gray-100 border-gray-300'
                      }`}>
                        {isJoin ? (
                          <UserPlus className="w-4 h-4 text-green-600" />
                        ) : (
                          <UserMinus className="w-4 h-4 text-gray-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 pl-3 py-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-medium ${isJoin ? 'text-green-600' : 'text-gray-500'}`}>
                            {name}
                          </span>
                          <span className={`text-sm ${isJoin ? 'text-green-600' : 'text-gray-500'}`}>
                            {isJoin ? 'joined' : 'left'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatTimestamp(event.at)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            ({formatRelativeTime(event.at)})
                          </span>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              });
            })()}

            {/* Session started event - at bottom */}
            <div className="flex items-start mt-4">
              <div className="w-14 shrink-0" />
              <div className="relative z-10 flex items-center justify-center w-10 h-10 rounded-full border-2 bg-cyan-100 border-cyan-300">
                <Flag className="w-4 h-4 text-cyan-600" />
              </div>
              <div className="flex-1 min-w-0 pl-3 py-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-cyan-600">Session Started</span>
                  <span className="text-xs text-muted-foreground">
                    {formatTimestamp(session.startedAt)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ({formatRelativeTime(session.startedAt)})
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default IglooSessionTimeline;
